// crewbit-admin/src/components/layout/SidebarMoneyNavItem.tsx
//
// Extracted from Sidebar.tsx at §T.80 commit C3 attempt — Sidebar.tsx hit
// Golden Rule #14 (≤500 lines) at 534 lines after Phase B Money sub-nav
// extension landed inline. Same fix-not-bypass discipline as §T.76's
// InvoicesPageBranches.tsx extraction.
//
// SPEC §Money.NavigationLayerRealization Phase B (D9-D17). The Money sidebar
// entry is special: two interaction targets (main click area navigates to
// /money; chevron caret toggles expand/collapse without navigating);
// auto-expand-when-active overrides saved state per D9; sessionStorage
// persistence per D10 (auth-id-scoped per-tab isolation per Money DATA Q1);
// useFinancialSummary second-consumer instance per D14 for sub-item badges.
//
// Hover state (`hovered` / `setHovered`) is owned by Sidebar so all sidebar
// entries share a single hover slot — passed in as props.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { transition, sidebar as sb } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useFinancialSummary } from '../../hooks/useFinancialSummary';
import {
  resolveMoneySubItems,
  getMoneySubItemBadge,
  type MoneySubItem,
} from './sidebarMoneySubItems';

const SIDEBAR_MONEY_EXPANDED_KEY = (authId: string) => `sidebar:money:expanded:${authId}`;
const MONEY_SUB_ITEMS_DOM_ID = 'money-sub-items';

export interface SidebarMoneyNavItemProps {
  item: {
    id: string;
    path: string;
    label: string;
    icon: React.ComponentType<{ size?: number; strokeWidth?: number }>;
  };
  badge?: number;
  collapsed: boolean;
  activePath: string;
  hovered: string | null;
  setHovered: (id: string | null) => void;
  // C3 P3 — active industry cluster ('A'–'G') for per-cluster sub-item gating.
  cluster?: string | null;
}

export function SidebarMoneyNavItem({
  item,
  badge,
  collapsed,
  activePath,
  hovered,
  setHovered,
  cluster,
}: SidebarMoneyNavItemProps) {
  const navigate = useNavigate();
  const authId = useAuthStore((s) => s.user?.id ?? null);
  const moneyActive = activePath.startsWith('/money');

  const [moneyExpandedSaved, setMoneyExpandedSaved] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !authId) return false;
    try {
      return window.sessionStorage.getItem(SIDEBAR_MONEY_EXPANDED_KEY(authId)) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !authId) return;
    try {
      window.sessionStorage.setItem(
        SIDEBAR_MONEY_EXPANDED_KEY(authId),
        moneyExpandedSaved ? '1' : '0',
      );
    } catch {
      /* ignore — quota exceeded, private mode, etc. */
    }
  }, [moneyExpandedSaved, authId]);

  const moneyExpanded = moneyActive || moneyExpandedSaved;
  const toggleMoneyExpanded = () => setMoneyExpandedSaved((v) => !v);

  const moneySummary = useFinancialSummary({ period: '30d' });
  const moneyChevronRef = useRef<HTMLButtonElement>(null);
  const moneySubItemsRef = useRef<HTMLUListElement>(null);

  const isActive = activePath === item.path;
  const isParentActive = activePath.startsWith(item.path);
  const isHov = hovered === item.id;
  const Icon = item.icon;
  // C3 P3 — per-cluster sub-item gating (identity until the seed in
  // sidebarMoneySubItems.ts is populated).
  const subItems = resolveMoneySubItems(cluster);

  const handleSubItemsKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (!moneySubItemsRef.current) return;
    const items = Array.from(
      moneySubItemsRef.current.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
    );
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[Math.min(idx + 1, items.length - 1)] ?? items[0];
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[Math.max(idx - 1, 0)] ?? items[0];
      prev?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMoneyExpandedSaved(false);
      moneyChevronRef.current?.focus();
    }
  };

  return (
    <>
      <div
        role="presentation"
        style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          margin: '1px 8px',
          position: 'relative',
        }}
      >
        {isParentActive && (
          <div style={{
            position: 'absolute', left: -8, top: 6, bottom: 6,
            width: 3, borderRadius: '0 3px 3px 0', background: sb.accentBar,
            pointerEvents: 'none',
          }} />
        )}
        <button
          title={collapsed ? item.label : undefined}
          onClick={() => navigate(item.path)}
          onMouseEnter={() => setHovered(item.id)}
          onMouseLeave={() => setHovered(null)}
          aria-current={isActive ? 'page' : undefined}
          style={{
            display: 'flex', alignItems: 'center',
            gap: collapsed ? 0 : 10,
            justifyContent: collapsed ? 'center' : undefined,
            padding: collapsed ? '8px' : '8px 16px',
            borderRadius: 8,
            border: 'none', cursor: 'pointer',
            background: isParentActive ? sb.bgActive : isHov ? sb.bgHover : 'transparent',
            transition: `all ${transition.fast}`,
            fontFamily: 'inherit',
            flex: 1, minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <span style={{
            color: isParentActive ? sb.textActive : isHov ? sb.textHover : sb.text,
            display: 'flex', transition: 'color 0.12s', flexShrink: 0,
            opacity: isParentActive ? 1 : 0.7,
          }}>
            <Icon size={18} strokeWidth={1.8} />
          </span>
          <span style={{
            fontSize: 13, fontWeight: isParentActive ? 550 : 450, letterSpacing: '-0.01em',
            color: isParentActive ? sb.textActive : isHov ? sb.textHover : sb.text,
            flex: 1, textAlign: 'left',
            opacity: collapsed ? 0 : 1, transition: `opacity 150ms ease`,
            width: collapsed ? 0 : undefined, overflow: 'hidden', whiteSpace: 'nowrap',
          }}>{item.label}</span>
          {!collapsed && badge !== undefined && badge > 0 && (
            <span style={{
              background: '#0F6CBD', color: '#FFFFFF',
              fontSize: 10, fontWeight: 600,
              padding: '1px 6px', borderRadius: 10, minWidth: 18, textAlign: 'center',
            }}>{badge}</span>
          )}
        </button>
        {!collapsed && (
          <button
            ref={moneyChevronRef}
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleMoneyExpanded(); }}
            aria-label={moneyExpanded ? 'Collapse Money sub-items' : 'Expand Money sub-items'}
            aria-expanded={moneyExpanded}
            aria-controls={MONEY_SUB_ITEMS_DOM_ID}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 28,
              marginLeft: 2,
              border: 'none', borderRadius: 6,
              background: 'transparent',
              cursor: 'pointer',
              color: isParentActive ? sb.textActive : sb.text,
              opacity: 0.7,
              transition: `background ${transition.fast}, transform ${transition.fast}, opacity ${transition.fast}`,
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = sb.bgHover; e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.7'; }}
          >
            {moneyExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        )}
      </div>
      {/* C3 P3 — smooth height transition on expand/collapse via the grid-rows
          0fr↔1fr trick (no JS measurement). The list stays mounted while
          !collapsed so the collapse can animate; when closed it is clipped to
          0 height AND made aria-hidden + non-tabbable so the observable ARIA /
          keyboard behavior is byte-equivalent to the prior mount/unmount. The
          chevron's aria-expanded/aria-controls + sessionStorage persistence are
          unchanged. */}
      {!collapsed && (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: moneyExpanded ? '1fr' : '0fr',
            transition: `grid-template-rows ${transition.fast}`,
          }}
        >
          <div style={{ overflow: 'hidden', minHeight: 0 }}>
            <ul
              id={MONEY_SUB_ITEMS_DOM_ID}
              ref={moneySubItemsRef}
              role="group"
              aria-label="Money sub-navigation"
              aria-hidden={!moneyExpanded || undefined}
              onKeyDown={handleSubItemsKeyDown}
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '4px 8px 8px',
              }}
            >
              {subItems.map((subItem) => (
                <MoneySubItemRow
                  key={subItem.id}
                  subItem={subItem}
                  activePath={activePath}
                  hovered={hovered}
                  setHovered={setHovered}
                  navigate={navigate}
                  summaryData={moneySummary.data}
                  tabbable={moneyExpanded}
                />
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

interface MoneySubItemRowProps {
  subItem: MoneySubItem;
  activePath: string;
  hovered: string | null;
  setHovered: (id: string | null) => void;
  navigate: ReturnType<typeof useNavigate>;
  summaryData: ReturnType<typeof useFinancialSummary>['data'];
  // C3 P3 — false while the list is collapsed (clipped-but-mounted for the
  // animation) so collapsed sub-items stay out of the tab order.
  tabbable: boolean;
}

function MoneySubItemRow({
  subItem,
  activePath,
  hovered,
  setHovered,
  navigate,
  summaryData,
  tabbable,
}: MoneySubItemRowProps) {
  const subActive = activePath.startsWith(subItem.path);
  const subHov = hovered === subItem.id;
  const SubIcon = subItem.icon;
  const badge = getMoneySubItemBadge(subItem.id, summaryData);

  return (
    <li role="none" style={{ listStyle: 'none' }}>
      <button
        role="menuitem"
        type="button"
        tabIndex={tabbable ? 0 : -1}
        onClick={() => navigate(subItem.path)}
        onMouseEnter={() => setHovered(subItem.id)}
        onMouseLeave={() => setHovered(null)}
        aria-current={subActive ? 'page' : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          width: '100%',
          padding: '6px 12px 6px 36px',
          margin: '1px 0',
          border: 'none', borderRadius: 8,
          background: subActive ? sb.bgActive : subHov ? sb.bgHover : 'transparent',
          cursor: 'pointer',
          transition: `all ${transition.fast}`,
          position: 'relative',
          fontFamily: 'inherit',
          textAlign: 'left',
        }}
      >
        {subActive && (
          <div style={{
            position: 'absolute', left: -8, top: 4, bottom: 4,
            width: 3, borderRadius: '0 3px 3px 0', background: sb.accentBar,
          }} />
        )}
        <span style={{
          color: subActive ? sb.textActive : subHov ? sb.textHover : sb.text,
          display: 'flex', flexShrink: 0, opacity: subActive ? 1 : 0.7,
        }}>
          <SubIcon size={15} strokeWidth={1.8} />
        </span>
        <span style={{
          fontSize: 12.5, fontWeight: subActive ? 550 : 400, letterSpacing: '-0.01em',
          color: subActive ? sb.textActive : subHov ? sb.textHover : sb.text,
          flex: 1, textAlign: 'left', whiteSpace: 'nowrap',
        }}>
          {subItem.label}
        </span>
        {badge && (
          <span style={{
            background: badge.color, color: '#FFFFFF',
            fontSize: 10, fontWeight: 600,
            padding: '1px 6px', borderRadius: 10, minWidth: 16, textAlign: 'center',
          }}>{badge.value}</span>
        )}
      </button>
    </li>
  );
}
